// useChatGrupos — hooks pra grupos internos no inbox.
// Reusa whatsapp_conversations (filtrado por chat_grupo_id) — não cria
// nada novo no fluxo de listagem; ChatWindow renderiza a conversa
// exatamente como qualquer outra.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ChatGrupo {
  id: string;
  nome: string;
  tipo: 'geral' | 'equipe' | 'custom' | 'whatsapp';
  equipe_id: string | null;
  criado_por: string | null;
  ativo: boolean;
  icone_url: string | null;
  descricao: string | null;
  created_at: string;
  conversation_id?: string | null;
  membros_ativos?: number;
  /** True quando o usuário corrente fixou o grupo (sempre true pro Geral). */
  fixado?: boolean;
}

export interface ChatGrupoMembro {
  grupo_id: string;
  usuario_id: string;
  ativo: boolean;
  papel: 'membro' | 'admin';
  added_at: string;
  usuario?: {
    id: string;
    nome: string;
    foto_url: string | null;
    role: string;
    equipe_id: string | null;
  };
}

// Lista grupos visíveis ao usuário (RLS resolve).
export function useChatGrupos() {
  return useQuery({
    queryKey: ['chat-grupos'],
    queryFn: async (): Promise<ChatGrupo[]> => {
      const { data: meRes } = await supabase.auth.getUser();
      const myId = meRes?.user?.id;

      const { data, error } = await supabase
        .from('chat_grupos')
        .select(`
          id, nome, tipo, equipe_id, criado_por, ativo, icone_url, descricao, created_at
        `)
        .eq('ativo', true)
        .order('tipo')
        .order('nome');
      if (error) throw error;

      const grupoIds = (data || []).map((g) => g.id);
      if (grupoIds.length === 0) return [];

      const { data: convs } = await supabase
        .from('whatsapp_conversations')
        .select('id, chat_grupo_id')
        .in('chat_grupo_id', grupoIds);

      const convMap = new Map((convs || []).map((c) => [c.chat_grupo_id, c.id]));

      const { data: cnt } = await supabase
        .from('chat_grupos_membros')
        .select('grupo_id, ativo')
        .in('grupo_id', grupoIds)
        .eq('ativo', true);

      const cntMap = new Map<string, number>();
      for (const m of cnt || []) {
        cntMap.set(m.grupo_id, (cntMap.get(m.grupo_id) || 0) + 1);
      }

      // Fixados do usuário corrente
      const fixadoMap = new Map<string, boolean>();
      if (myId) {
        const { data: minhas } = await supabase
          .from('chat_grupos_membros')
          .select('grupo_id, fixado')
          .eq('usuario_id', myId)
          .eq('ativo', true)
          .in('grupo_id', grupoIds);
        for (const m of minhas || []) {
          fixadoMap.set(m.grupo_id, !!m.fixado);
        }
      }

      return (data || []).map((g) => ({
        ...g,
        conversation_id: convMap.get(g.id) || null,
        membros_ativos: cntMap.get(g.id) || 0,
        // Geral é forçado fixado mesmo se a row de membros não estiver
        fixado: g.tipo === 'geral' || g.tipo === 'whatsapp' ? true : (fixadoMap.get(g.id) || false),
      })) as ChatGrupo[];
    },
    staleTime: 30_000,
  });
}

// Toggle fixar/desafixar grupo pra usuário corrente.
export function useToggleFixarGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { grupo_id: string; fixar: boolean }) => {
      const { error } = await supabase.rpc('chat_grupo_toggle_fixar', {
        p_grupo_id: input.grupo_id,
        p_fixar: input.fixar,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-grupos'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
    onError: (e: any) => {
      toast.error('Falha ao fixar: ' + (e?.message || String(e)));
    },
  });
}

// Membros de um grupo específico.
export function useChatGrupoMembros(grupoId: string | null | undefined) {
  return useQuery({
    queryKey: ['chat-grupo-membros', grupoId],
    enabled: !!grupoId,
    queryFn: async (): Promise<ChatGrupoMembro[]> => {
      const { data, error } = await supabase
        .from('chat_grupos_membros')
        .select(`
          grupo_id, usuario_id, ativo, papel, added_at,
          usuario:usuarios!chat_grupos_membros_usuario_id_fkey ( id, nome, foto_url, role, equipe_id )
        `)
        .eq('grupo_id', grupoId!)
        .order('papel') // admin primeiro
        .order('added_at');
      if (error) throw error;
      return (data || []) as any;
    },
    staleTime: 30_000,
  });
}

// Cria grupo custom (só master).
export function useCriarGrupoCustom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      nome: string;
      descricao?: string;
      icone_url?: string;
      membros: string[];
    }) => {
      const { data, error } = await supabase.rpc('chat_grupo_criar_custom', {
        p_nome: input.nome,
        p_descricao: input.descricao || null,
        p_icone_url: input.icone_url || null,
        p_membros: input.membros,
      });
      if (error) throw error;
      return data as string; // grupo_id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-grupos'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      toast.success('Grupo criado');
    },
    onError: (e: any) => {
      toast.error('Falha ao criar grupo: ' + (e?.message || String(e)));
    },
  });
}

// Toggle membro (ativar/desativar). Só master.
export function useToggleMembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { grupo_id: string; usuario_id: string; ativo: boolean }) => {
      const { error } = await supabase.rpc('chat_grupo_membro_toggle', {
        p_grupo_id: input.grupo_id,
        p_usuario_id: input.usuario_id,
        p_ativo: input.ativo,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['chat-grupo-membros', vars.grupo_id] });
      qc.invalidateQueries({ queryKey: ['chat-grupos'] });
    },
    onError: (e: any) => {
      toast.error('Falha ao atualizar membro: ' + (e?.message || String(e)));
    },
  });
}

// Edita grupo (nome/ícone/descrição). Só master.
export function useEditarGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      grupo_id: string;
      nome?: string;
      descricao?: string;
      icone_url?: string;
    }) => {
      const { error } = await supabase.rpc('chat_grupo_editar', {
        p_grupo_id: input.grupo_id,
        p_nome: input.nome || null,
        p_descricao: input.descricao || null,
        p_icone_url: input.icone_url || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-grupos'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
    onError: (e: any) => {
      toast.error('Falha ao editar grupo: ' + (e?.message || String(e)));
    },
  });
}

export interface GrupoNaoLidas {
  grupo_id: string;
  nao_lidas: number;
  ultima_msg: string | null;
}

// Não lidas + última mensagem por grupo (badge e ordenação).
export function useGruposNaoLidas() {
  return useQuery({
    queryKey: ['chat-grupos-nao-lidas'],
    queryFn: async (): Promise<Map<string, GrupoNaoLidas>> => {
      const { data, error } = await supabase.rpc('chat_grupo_nao_lidas');
      if (error) throw error;
      const map = new Map<string, GrupoNaoLidas>();
      for (const r of (data || []) as any[]) {
        map.set(r.grupo_id, {
          grupo_id: r.grupo_id,
          nao_lidas: Number(r.nao_lidas || 0),
          ultima_msg: r.ultima_msg || null,
        });
      }
      return map;
    },
    staleTime: 10_000,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
}

// Marca o grupo como lido (abre a conversa / chega no fim).
export function useMarcarGrupoLido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (grupoId: string) => {
      const { error } = await supabase.rpc('chat_grupo_marcar_lido', { p_grupo_id: grupoId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-grupos-nao-lidas'] });
    },
  });
}

export interface ReacaoMensagem {
  message_id: string;
  usuario_id: string;
  emoji: string;
  usuario?: { nome: string | null } | null;
}

// Reações das mensagens de uma conversa de grupo (via RPC, permissão no banco).
export function useReacoesConversa(conversationId: string | null | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ['chat-grupo-reacoes', conversationId],
    enabled: !!conversationId && habilitado,
    queryFn: async (): Promise<Map<string, ReacaoMensagem[]>> => {
      const { data, error } = await supabase.rpc('wa_msg_reacoes', {
        p_conversation_id: conversationId!,
      });
      if (error) throw error;
      const map = new Map<string, ReacaoMensagem[]>();
      for (const r of (data || []) as any[]) {
        const arr = map.get(r.message_id) || [];
        arr.push({ message_id: r.message_id, usuario_id: r.usuario_id, emoji: r.emoji, usuario: { nome: r.usuario_nome } });
        map.set(r.message_id, arr);
      }
      return map;
    },
    staleTime: 3_000,
    refetchInterval: 8_000,
  });
}

// "Fulano está digitando..." — heartbeat + polling leve (só com o grupo aberto).
export function useTypingGrupo(grupoId: string | null | undefined) {
  return useQuery({
    queryKey: ['chat-grupo-typing', grupoId],
    enabled: !!grupoId,
    queryFn: async (): Promise<{ usuario_id: string; nome: string | null }[]> => {
      const { data, error } = await supabase.rpc('chat_grupo_typing_list', { p_grupo_id: grupoId! });
      if (error) return [];
      return (data || []) as any[];
    },
    refetchInterval: 3_000,
    staleTime: 2_000,
  });
}

let __lastTypingEmit = 0;
// Emite "estou digitando" no máx a cada 3,5s (o servidor considera 6s vivo).
export function emitirTypingGrupo(grupoId: string) {
  const now = Date.now();
  if (now - __lastTypingEmit < 3_500) return;
  __lastTypingEmit = now;
  supabase.rpc('chat_grupo_typing', { p_grupo_id: grupoId }).then(() => {});
}

// Reagir em CONVERSA REAL (Evolution): backend grava o chip e manda a
// reação pro WhatsApp do cliente (sendReaction). Toggle igual ao grupo.
export function useReagirMensagemReal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { message_id: string; emoji: string; conversation_id: string }) => {
      const { data, error } = await supabase.functions.invoke('whatsapp-evolution-react', {
        body: { message_id: input.message_id, emoji: input.emoji },
      });
      if (error) throw new Error(error.message);
      if (data && data.success === false) throw new Error(data.erro_detalhe || 'Falha ao reagir');
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['chat-grupo-reacoes', vars.conversation_id] });
    },
    onError: (e: any) => {
      toast.error('Falha ao reagir: ' + (e?.message || String(e)));
    },
  });
}

let __lastPresenceEmit = 0;
// "Digitando..." no WhatsApp do cliente (conversa real) — no máx a cada 4s.
export function emitirPresencaDigitando(conversationId: string) {
  const now = Date.now();
  if (now - __lastPresenceEmit < 4_000) return;
  __lastPresenceEmit = now;
  supabase.functions.invoke('whatsapp-evolution-presence', {
    body: { conversation_id: conversationId },
  }).catch(() => {});
}

// Reagir a uma mensagem (toggle: mesmo emoji remove; novo substitui).
export function useReagirMensagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { message_id: string; emoji: string; conversation_id: string }) => {
      const { error } = await supabase.rpc('chat_msg_reagir', {
        p_message_id: input.message_id,
        p_emoji: input.emoji,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['chat-grupo-reacoes', vars.conversation_id] });
    },
    onError: (e: any) => {
      toast.error('Falha ao reagir: ' + (e?.message || String(e)));
    },
  });
}

export interface LeituraMembro {
  usuario_id: string;
  last_read_at: string;
}

// Leituras dos membros do grupo (recibo ✓✓ = todos leram).
export function useLeiturasGrupo(grupoId: string | null | undefined) {
  return useQuery({
    queryKey: ['chat-grupo-leituras', grupoId],
    enabled: !!grupoId,
    queryFn: async (): Promise<LeituraMembro[]> => {
      const { data, error } = await supabase.rpc('chat_grupo_leituras', { p_grupo_id: grupoId! });
      if (error) throw error;
      return (data || []) as LeituraMembro[];
    },
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

// Sair do grupo (auto-remoção; geral não permite).
export function useSairGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (grupoId: string) => {
      const { error } = await supabase.rpc('chat_grupo_sair', { p_grupo_id: grupoId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-grupos'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      toast.success('Você saiu do grupo');
    },
    onError: (e: any) => {
      toast.error('Falha ao sair: ' + (e?.message || String(e)));
    },
  });
}

// Promover/rebaixar admin (master ou admin do grupo).
export function useToggleAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { grupo_id: string; usuario_id: string; admin: boolean }) => {
      const { error } = await supabase.rpc('chat_grupo_toggle_admin', {
        p_grupo_id: input.grupo_id,
        p_usuario_id: input.usuario_id,
        p_admin: input.admin,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['chat-grupo-membros', vars.grupo_id] });
    },
    onError: (e: any) => {
      toast.error('Falha ao alterar admin: ' + (e?.message || String(e)));
    },
  });
}

// Apagar mensagem (autor, admin do grupo ou master) — vira "Mensagem apagada".
export function useApagarMensagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { message_id: string; conversation_id: string }) => {
      const { error } = await supabase.rpc('chat_msg_apagar', { p_message_id: input.message_id });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['whatsapp-messages', vars.conversation_id] });
      qc.invalidateQueries({ queryKey: ['messages', vars.conversation_id] });
    },
    onError: (e: any) => {
      toast.error('Falha ao apagar: ' + (e?.message || String(e)));
    },
  });
}

// Notifica os mencionados (@pessoa) de uma mensagem enviada.
export async function notificarMencoes(grupoId: string, usuarioIds: string[], preview: string) {
  if (!usuarioIds.length) return;
  await supabase.rpc('chat_msg_mencionar', {
    p_grupo_id: grupoId,
    p_usuarios: usuarioIds,
    p_preview: preview,
  });
}

// Upload de ícone do grupo no bucket whatsapp-media.
// Retorna a URL pública.
export async function uploadIconeGrupo(file: File, grupoId: string): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `grupos/${grupoId}/icone-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('whatsapp-media')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('whatsapp-media').getPublicUrl(path);
  return data.publicUrl;
}

// ── Grupo do WhatsApp: quem escreveu ──────────────────────────────────────
// A Bee entrega a mensagem do grupo como "**Nome**:\n(555196087088)\n\ntexto".
// O telefone casa com o cadastro do usuário para mostrar foto e equipe.
// Chave = DDD + 8 últimos dígitos: o WhatsApp manda sem o 9 e o cadastro às
// vezes tem; comparar só os 8 finais casava gente de DDD diferente.

export interface AutorGrupoWhats {
  id: string;
  nome: string | null;
  foto_url: string | null;
  equipe_nome: string | null;
  equipe_cor: string | null;
}

export function chaveTelefoneGrupo(tel: string | null | undefined): string | null {
  let d = String(tel || '').replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  if (d.length < 10) return null;
  return d.slice(0, 2) + d.slice(-8);
}

const CABECALHO_AUTOR = /^\*\*(.+?)\*\*:\s*\n\((\d{8,15})\)\s*\n*/;

export function lerAutorGrupoWhats(body: string | null | undefined) {
  const m = CABECALHO_AUTOR.exec(body || '');
  if (!m) return null;
  return { nome: m[1].trim(), telefone: m[2], texto: (body || '').slice(m[0].length) };
}

export function useAutoresGrupoWhats(enabled: boolean) {
  return useQuery({
    queryKey: ['grupo-whats-autores'],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [{ data: us }, { data: eqs }] = await Promise.all([
        supabase.from('usuarios').select('id, nome, foto_url, telefone, equipe_id').eq('ativo', true),
        supabase.from('equipes').select('id, nome, cor'),
      ]);
      type EquipeRow = { id: string; nome: string | null; cor: string | null };
      type UsuarioRow = { id: string; nome: string | null; foto_url: string | null; telefone: string | null; equipe_id: string | null };
      const equipes = new Map(((eqs || []) as EquipeRow[]).map((e) => [e.id, e]));
      const porId = new Map<string, AutorGrupoWhats>();
      const porTelefone = new Map<string, AutorGrupoWhats>();
      for (const u of (us || []) as UsuarioRow[]) {
        const eq = u.equipe_id ? equipes.get(u.equipe_id) : null;
        const autor: AutorGrupoWhats = {
          id: u.id,
          nome: u.nome,
          foto_url: u.foto_url,
          equipe_nome: eq?.nome ?? null,
          equipe_cor: eq?.cor ?? null,
        };
        porId.set(u.id, autor);
        const chave = chaveTelefoneGrupo(u.telefone);
        if (chave && !porTelefone.has(chave)) porTelefone.set(chave, autor);
      }
      return { porId, porTelefone };
    },
  });
}
